import { encryptData, decryptData, initDB } from "@/service/indexedDBService";
import { isset } from "@/allfunction";

// Connexion partagée du module indexedDBService : une seule par onglet, et
// qui se ferme d'elle-même quand un autre onglet applique une nouvelle
// version. L'ancien openDB(DB_NAME, DB_VERSION) local ouvrait une connexion
// par appel et, surtout, n'avait aucun callback upgrade : si la base était
// absente, il en créait une vide, sans le moindre store.
async function getDB() {
    return initDB();
}

function pad(n) {
    return String(n).padStart(3, '0');
}

function todayPrefix() {
    const d = new Date();
    return String(d.getDate()).padStart(2,'0') + String(d.getMonth()+1).padStart(2,'0');
}

/* ── Wait Number ── */
/*export async function getNextWaitNumber() {
    const db = await getDB();
    const prefix = todayPrefix();
    const tx = db.transaction("waitNumberCounter", "readwrite");
    const store = tx.objectStore("waitNumberCounter");
    const all = await store.getAll();
    let record = all.find(r => {
        try { return decryptData(r.datePrefix) === prefix; } catch(e) { return false; }
    });
    if (!record) {
        record = { datePrefix: encryptData(prefix), counter: 1 };
        record.id = await store.add(record);
    } else {
        record.counter++;
        await store.put(record);
    }
    await tx.done;
    return 'W-' + prefix + '-' + pad(record.counter);
}*/

// --- Schéma DB (dans ton onupgradeneeded) ---
// store "waitNumberCounter" : keyPath "datePrefix" (clé primaire = la date, plus besoin d'id auto)
// store "waitNumbers"       : keyPath "id", autoIncrement: true, index sur "datePrefix" et "createdAt"

export async function getNextWaitNumber(meta = {}) {
    const db = await getDB();
    const prefix = todayPrefix();

    const tx = db.transaction(["waitNumberCounter", "waitNumbers"], "readwrite");
    const counterStore = tx.objectStore("waitNumberCounter");
    const historyStore = tx.objectStore("waitNumbers");

    // Lecture directe via la clé primaire = prefix (O(1), pas de getAll ni de déchiffrement)
    let record = await counterStore.get(prefix);

    if (!record) {
        record = { datePrefix: prefix, counter: 1 };
    } else {
        record.counter++;
    }
    await counterStore.put(record);

    const waitNumber = 'W-' + prefix + '-' + pad(record.counter);

    // Enregistrement de l'historique du numéro généré
    await historyStore.add({
        number: waitNumber,
        datePrefix: prefix,
        counter: record.counter,
        createdAt: new Date().toISOString(),
        ...meta // ex: { clientId, guichet, agentId } si tu veux tracer qui/quoi
    });

    await tx.done;
    return waitNumber;
}

/* ── Clé technique du ticket ──
   W-JJMM-NNN n'est unique que dans ce navigateur. La clé envoyée à l'API
   (pidindexedb : registre d'idempotence, verrou) reçoit donc un code tiré au
   hasard une fois par navigateur : deux appareils sur la même caisse le même
   jour ne partagent plus de clé. L'affichage garde W-JJMM-NNN.
   Le code vit dans le même store que le compteur : si le navigateur perd l'un,
   il perd l'autre, et un nouveau code est tiré quand le compteur repart à 001. */
const CLE_APPAREIL = 'appareil';

function tirerCode() {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const octets = new Uint8Array(4);
    crypto.getRandomValues(octets);
    return Array.from(octets, o => alphabet[o % alphabet.length]).join('');
}

async function codeAppareil() {
    const db = await getDB();
    const tx = db.transaction('waitNumberCounter', 'readwrite');
    const store = tx.objectStore('waitNumberCounter');
    let record = await store.get(CLE_APPAREIL);
    if (!record || !record.code) {
        record = { datePrefix: CLE_APPAREIL, code: tirerCode() };
        await store.put(record);
    }
    await tx.done;
    return record.code;
}

// Clé technique d'une commande : W-JJMM-NNN-CODE
export async function getNextTicketKey(meta = {}) {
    const numero = await getNextWaitNumber(meta);
    return numero + '-' + await codeAppareil();
}

// Numéro d'attente à afficher : W-JJMM-NNN (sans le code appareil)
export function numeroAffiche(cle) {
    const m = /^(W-\d{4}-\d{3,})(-[A-Z0-9]{4})?$/.exec(cle || '');
    return m ? m[1] : (cle || '');
}

export async function resetWaitNumberCounter() {
    const db = await getDB();
    const tx = db.transaction("waitNumberCounter", "readwrite");
    const store = tx.objectStore("waitNumberCounter");
    await store.clear();
    await tx.done;
}

/* ── Corbeille d'audit ──
   Toute suppression dans orderQueue est archivée dans orderQueueTrash AVANT
   d'être effectuée. Les deux stores vivant dans la même base, l'archivage et
   la suppression tiennent dans une seule transaction : ni perte ni doublon si
   l'opération est interrompue.

   Le payload est recopié TEL QUEL, toujours chiffré - on ne déchiffre jamais
   pour archiver, ce qui évite d'écrire du clair sur le disque.

   À appeler DANS une transaction ouverte sur ["orderQueue","orderQueueTrash"],
   sans await entre l'appel et la suppression : un await hors du flux de la
   transaction la ferait expirer. */
function archiveToTrash(trashStore, record, reason) {
    trashStore.add({
        originalId:        record.id,
        localId:           record.localId,
        status:            record.status,
        serverDocId:       record.serverDocId || '',
        srvdocnum:         record.srvdocnum   || '',
        srvpid:            record.srvpid      || '',
        errorMessage:      record.errorMessage || '',
        source:            record.source      || '',
        _syncedFromServer: record._syncedFromServer || false,
        createdAt:         record.createdAt,
        updatedAt:         record.updatedAt,
        encryptedPayload:  record.encryptedPayload,
        deletedAt:         Date.now(),
        reason,
    });
}

/* Contenu de la corbeille, du plus récent au plus ancien. Le payload reste
   chiffré : passer decrypt=true pour le déchiffrer à la lecture. */
export async function getTrash(limit = 200, decrypt = false) {
    const db = await getDB();
    const tx = db.transaction("orderQueueTrash", "readonly");
    const all = await tx.objectStore("orderQueueTrash").getAll();
    await tx.done;
    return all
        .sort((a, b) => (b.deletedAt || 0) - (a.deletedAt || 0))
        .slice(0, limit)
        .map(r => {
            if (!decrypt) return r;
            let orderData = null;
            try { orderData = decryptData(r.encryptedPayload); } catch (e) { /* archive illisible */ }
            return { ...r, orderData };
        });
}

/* Rétention de la corbeille. Sans borne elle grandit indéfiniment. */
export async function purgeTrash(retentionJours = 30) {
    try {
        const limite = Date.now() - retentionJours * 24 * 3600 * 1000;
        const db = await getDB();
        const tx = db.transaction("orderQueueTrash", "readwrite");
        const store = tx.objectStore("orderQueueTrash");
        let cursor = await store.openCursor();
        let deleted = 0;
        while (cursor) {
            if ((cursor.value.deletedAt || 0) < limite) { cursor.delete(); deleted++; }
            cursor = await cursor.continue();
        }
        await tx.done;
        return deleted;
    } catch (e) {
        console.warn('[purgeTrash] Erreur nettoyage:', e);
        return 0;
    }
}

/* Remet une commande archivée dans la file. L'entrée de corbeille est
   conservée : la restauration est elle-même un événement traçable. */
export async function restoreFromTrash(trashId) {
    const db = await getDB();
    const tx = db.transaction(["orderQueue", "orderQueueTrash"], "readwrite");
    const archive = await tx.objectStore("orderQueueTrash").get(trashId);
    if (!archive) { await tx.done; return { success: false, message: 'Archive introuvable' }; }

    tx.objectStore("orderQueue").add({
        localId:           archive.localId,
        serverDocId:       archive.serverDocId,
        _syncedFromServer: archive._syncedFromServer,
        status:            archive.status,
        source:            archive.source || 'local',
        errorMessage:      archive.errorMessage,
        srvdocnum:         archive.srvdocnum,
        srvpid:            archive.srvpid,
        encryptedPayload:  archive.encryptedPayload,
        createdAt:         archive.createdAt,
        updatedAt:         Date.now(),
    });
    await tx.done;
    return { success: true, localId: archive.localId };
}

/* ── Order Queue ── */
export async function addToQueue(localId, orderPayload) {
    //console.log('addToQueue called with localId:', localId, 'orderPayload:', orderPayload);
    const db = await getDB();
    const tx = db.transaction("orderQueue", "readwrite");
    const store = tx.objectStore("orderQueue");
    const record = {
        localId,
        /*listedatatableindexedb: orderPayload.listedatatableindexedb ,
        listedatatable: orderPayload.listedatatable ,
        Items: orderPayload.Items ,
        jsonv: orderPayload.jsonv ,
        jsonotherdonne: orderPayload.jsonotherdonne ,
        mtnt: orderPayload.mtnt ,
        client: orderPayload.client ,
        serveur: orderPayload.serveur ,
        commandetable: orderPayload.commandetable ,*/
        serverDocId: orderPayload.serverDocId || '',
        _syncedFromServer: orderPayload._syncedFromServer || false,
        status: 'pending',
        //source: orderPayload.source || 'local',
        source: 'local',
        errorMessage: '',
        srvdocnum: '',
        srvpid: '',
        encryptedPayload: encryptData(orderPayload),
        createdAt: Date.now(),
        updatedAt: Date.now(),
    };
    await store.add(record);
    await tx.done;
    return { success: true, message: 'Order added to queue', localId };
}

export async function updateQueueData(localId, newPayload) {
    const db = await getDB();
    const tx = db.transaction("orderQueue", "readwrite");
    const store = tx.objectStore("orderQueue");
    const all = await store.getAll();
    const record = all.find(r => r.localId === localId);
    if (record) {
        record.encryptedPayload = encryptData(newPayload);
        record.updatedAt = Date.now();
        record.source= 'local'
        record._syncedFromServer = newPayload._syncedFromServer;
        await store.put(record);
    }
    await tx.done;
}


export async function updateQueueDataItems(localId, listedatatableindexedb) {
    const db = await getDB();
    const tx = db.transaction("orderQueue", "readwrite");
    const store = tx.objectStore("orderQueue");
    const all = await store.getAll();
    const record = all.find(r => r.localId === localId);
    if (record) {

   let  orderData= decryptData(record.encryptedPayload)

            orderData._syncedFromServer=false
        
        record.updatedAt = Date.now();
        record.listedatatableindexedb = listedatatableindexedb;
        record.listedatatable = listedatatableindexedb;
        record.source= 'local'
        //record._syncedFromServer = newPayload._syncedFromServer;
         orderData.listedatatableindexedb = listedatatableindexedb;
        orderData.listedatatable = listedatatableindexedb;
        record.encryptedPayload = encryptData(orderData);


        await store.put(record);
    }
    await tx.done;
}



export async function updateQueueStatus(localId, status, serverDocId, errorMessage,srvdocnum='') {
    const db = await getDB();
    const tx = db.transaction("orderQueue", "readwrite");
    const store = tx.objectStore("orderQueue");
    const all = await store.getAll();
    const record = all.find(r => r.localId === localId);
    if (record) {

          let  orderData= decryptData(record.encryptedPayload)
 
        record.status = status;
        record.updatedAt = Date.now();

        if (isset(srvdocnum)) {
            orderData.Items.docnum = srvdocnum;
            orderData.srvdocnum = srvdocnum;
            record.srvdocnum = srvdocnum;
 

        }        
        //if (srvdocnum) record.srvdocnum = srvdocnum;
        if ( isset(serverDocId)) {

            record.srvpid = serverDocId;
            orderData.Items.docpid = serverDocId;
            orderData.Items.pid = serverDocId;
            orderData.srvpid = serverDocId;
            record.serverDocId = serverDocId; 
        }
       /* if (serverDocId) orderData.Items.docpid = serverDocId;
        if (serverDocId) orderData.Items.pid = serverDocId;
        if (serverDocId) record.serverDocId = serverDocId;*/
        if (errorMessage) {
            record.errorMessage = errorMessage;
        } 

        record.encryptedPayload = encryptData(orderData);
        await store.put(record);
    }
    await tx.done;
}

export async function removeFromQueue(localId) {
    const db = await getDB();
    const tx = db.transaction(["orderQueue", "orderQueueTrash"], "readwrite");
    const store = tx.objectStore("orderQueue");
    const all = await store.getAll();
    const record = all.find(r => r.localId === localId);
    if (record) {
        archiveToTrash(tx.objectStore("orderQueueTrash"), record, 'suppression-manuelle');
        await store.delete(record.id);
    }
    await tx.done;
}

export async function getAllPendingOrders() {
    const db = await getDB();
    const tx = db.transaction("orderQueue", "readonly");
    const store = tx.objectStore("orderQueue");
       const all = await store.getAll();
 

    const $resarray= all
        //.filter(r => r.status === 'pending' || r.status === 'failed')
        .map(r => ({
            id: r.id,
            localId: r.localId,
            serverDocId: r.serverDocId,
            srvdocnum: r.srvdocnum,
            status: r.status,
            errorMessage: r.errorMessage,
            createdAt: r.createdAt,
            updatedAt: r.updatedAt,
            orderData: decryptData(r.encryptedPayload),
        }));


return $resarray;

}

export async function getQueueCount() {
    const db = await getDB();
    const tx = db.transaction("orderQueue", "readonly");
    const store = tx.objectStore("orderQueue");
    const all = await store.getAll();
    return all.filter(r => r.status === 'pending' || r.status === 'failed').length;
}

export async function updateQueueServerDocId(localId, serverDocId) {
    await updateQueueStatus(localId, 'sent', serverDocId, '');
}

export async function _autoCleanCompleted_old() {
    try {
        console.log('[autoCleanCompleted] Nettoyage des commandes complétées dans IndexedDB…');
        const db = await getDB();
        const tx = db.transaction("orderQueue", "readwrite");
        const store = tx.objectStore("orderQueue");
        const all = await store.getAll();
        for (const r of all) {
            if (r.status === 'completed') {
                await store.delete(r.id);
            }
        }
        await tx.done;
    } catch (e) {
        console.warn('[autoCleanCompleted] Erreur nettoyage:', e);
    }
}

export async function _autoCleanCompleted() {
    try {
        const db = await getDB();
        const tx = db.transaction(["orderQueue", "orderQueueTrash"], "readwrite");
        const store = tx.objectStore("orderQueue");
        const trash = tx.objectStore("orderQueueTrash");

        // On ouvre un curseur qui va parcourir la base élément par élément
        let cursor = await store.openCursor();

        while (cursor) {
            if (cursor.value.status === 'completed') {
                archiveToTrash(trash, cursor.value, 'encaissement');
                // cursor.delete() est plus performant car il sait déjà où il est
                cursor.delete();
            }
            // On passe à l'élément suivant
            cursor = await cursor.continue();
        }

        await tx.done;
    } catch (e) {
        console.warn('[autoCleanCompleted] Erreur nettoyage:', e);
    }
}
 

export async function markQueueCompleted(localId,srvdocnum="",srvpid="") {
    await updateQueueStatus(localId, 'completed',srvpid, '',srvdocnum);
    await _autoCleanCompleted();
}

export async function markQueuePending(localId,srvdocnum="",srvpid="") {
    await updateQueueStatus(localId, 'pending',srvpid, '',srvdocnum);
} 

export async function markQueueFailed(localId, errorMessage) {
    await updateQueueStatus(localId, 'failed', '', errorMessage);
}

export async function getQueueOrderByLocalId(localId) {
    const db = await getDB();
    const tx = db.transaction("orderQueue", "readonly");
    const store = tx.objectStore("orderQueue");
    const all = await store.getAll();
    const record = all.find(r => r.localId === localId);
    if (!record) return null;
    return {
        id: record.id,
        localId: record.localId,
        serverDocId: record.serverDocId,
        status: record.status,
        errorMessage: record.errorMessage,
        createdAt: record.createdAt,
        updatedAt: record.updatedAt,
        orderData: decryptData(record.encryptedPayload),
    };
}

/* ── Print Counter ── */
export async function getPrintCounter(serverDocId) {
    const db = await getDB();
    const tx = db.transaction("printCounter", "readwrite");
    const store = tx.objectStore("printCounter");
    const all = await store.getAll();
    let record = all.find(r => {
        try { return decryptData(r.docnum) === serverDocId; } catch(e) { return false; }
    });
    if (!record) {
        record = {
            docnum: encryptData(serverDocId),
            cuisine: 0,
            bar: 0,
            caisse: 0,
        };
        record.id = await store.add(record);
        await tx.done;
        return { cuisine: 0, bar: 0, caisse: 0 };
    }
    await tx.done;
    return {
        cuisine: record.cuisine || 0,
        bar: record.bar || 0,
        caisse: record.caisse || 0,
    };
}

export async function incrementPrintCounter(serverDocId, printType) {
    const db = await getDB();
    const tx = db.transaction("printCounter", "readwrite");
    const store = tx.objectStore("printCounter");
    const all = await store.getAll();
    let record = all.find(r => {
        try { return decryptData(r.docnum) === serverDocId; } catch(e) { return false; }
    });
    if (!record) {
        record = {
            docnum: encryptData(serverDocId),
            cuisine: 0,
            bar: 0,
            caisse: 0,
        };
        record.id = await store.add(record);
    }
    record[printType] = (record[printType] || 0) + 1;
    await store.put(record);
    await tx.done;
    return record[printType];
}

export async function removePrintCounter(serverDocId) {
    const db = await getDB();
    const tx = db.transaction("printCounter", "readwrite");
    const store = tx.objectStore("printCounter");
    const all = await store.getAll();
    const record = all.find(r => {
        try { return decryptData(r.docnum) === serverDocId; } catch(e) { return false; }
    });
    if (record) {
        await store.delete(record.id);
    }
    await tx.done;
}

export async function clearAllPrintCounters() {
    const db = await getDB();
    const tx = db.transaction("printCounter", "readwrite");
    const store = tx.objectStore("printCounter");
    await store.clear();
    await tx.done;
}

/* ── Cleanup expired orders (24h) ── */
export async function cleanExpiredOrders() {
    const db = await getDB();
    const tx = db.transaction(["orderQueue", "orderQueueTrash"], "readwrite");
    const store = tx.objectStore("orderQueue");
    const trash = tx.objectStore("orderQueueTrash");
    const all = await store.getAll();
    const now = Date.now();
    const expiry = 24 * 3600 * 1000;
    for (const r of all) {
        if (r.createdAt && (now - r.createdAt > expiry) && r.status === 'completed') {
            archiveToTrash(trash, r, 'expiration-24h');
            await store.delete(r.id);
        }
    }
    await tx.done;
}
 
/* ── Purge sélective : conserve les commandes en échec ──
   Supprime tout SAUF le statut 'failed'. Une commande 'pending' a été
   acceptée par le serveur (markQueuePending), qui la restituera à la
   prochaine synchro ; une 'failed' n'existe que localement et serait
   perdue définitivement, avec son errorMessage et la possibilité de la
   relancer. */
export async function purgeQueueKeepFailed() {
    try {
        const db = await getDB();
        const tx = db.transaction(["orderQueue", "orderQueueTrash"], "readwrite");
        const store = tx.objectStore("orderQueue");
        const trash = tx.objectStore("orderQueueTrash");
        let cursor = await store.openCursor();
        let deleted = 0;
        while (cursor) {
            if (cursor.value.status !== 'failed') {
                archiveToTrash(trash, cursor.value, 'purge-refresh');
                cursor.delete();
                deleted++;
            }
            cursor = await cursor.continue();
        }
        await tx.done;
        return deleted;
    } catch (e) {
        console.warn('[purgeQueueKeepFailed] Erreur nettoyage:', e);
        return 0;
    }
}

/* Vidage TOTAL de la file, y compris les commandes 'failed'. Réservé à une
   action explicite : refreshData() utilise purgeQueueKeepFailed(). Tout ce qui
   est effacé passe d'abord par la corbeille. */
export async function _purgeorderQueueDB() {
    try {
        const db = await getDB();
        const tx = db.transaction(["orderQueue", "orderQueueTrash"], "readwrite");
        const store = tx.objectStore("orderQueue");
        const trash = tx.objectStore("orderQueueTrash");
        let cursor = await store.openCursor();
        while (cursor) {
            archiveToTrash(trash, cursor.value, 'vidage-total');
            cursor.delete();
            cursor = await cursor.continue();
        }
        await tx.done;
    } catch (e) {
        console.warn('[_purgeorderQueueDB] Erreur nettoyage:', e);
    }
}













